<script setup>
// import { JComments, JCommentInput } from "../../../vp-apps-ui-library/src/components";
import { JComments, JCommentInput } from "media-flow-ui"
// import { JComments, JCommentInput } from "../../../vp-apps-ui-library/src/components";
import { ref } from "vue"
import { usePageBuilder } from "~/stores/page-builder"
import { useAuthBuilder } from "~~/stores/auth-builder"
import Comments from "~~/models/layout/media/Comments"
import Reply from "~~/models/layout/media/Reply"
import { storeToRefs } from "pinia"
import Author from "~~/models/layout/media/Author"
import { useUrlBuilder } from "~/stores/url-builder"
import { $$t } from "~~/utils/i19m"

import { formatFontStyle } from "~/models/layout/style/font"
import { formatCommentsComponentStyle } from "~/models/layout/media/Comments"
import { formatIconStyle } from "~/helpers/icon/icon"
import Palette from "~/models/palette/Palette"

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: false,
  },
})

const pageBuilderStore = usePageBuilder()
const authBuilderStore = useAuthBuilder()
const urlBuilderStore = useUrlBuilder()

const { getCurrentUserAsAuthor } = storeToRefs(authBuilderStore)
const { deviceType, global } = storeToRefs(pageBuilderStore)
const { getProjectId, isCLH } = urlBuilderStore

const { data } = props

const commentsData = ref({ data: [] })

const mentions = ref([])
const page = ref(0)
const take = ref(10)
const count = ref(10)
const pages = ref(0)
const canComment = ref(true)
const headerHeight = ref(60)

const engagements = computed(() => data.components.engagements)

const style = computed(() => {
  const palette = new Palette()
  palette.checkColor(data.style.bgColor)
  return { backgroundColor: palette.color }
})

const titleStyle = computed(() =>
  formatFontStyle(data.components.title?.style.fontProperties)
)
const titleWrapperStyle = computed(() =>
  formatCommentsComponentStyle(data.components.title.style)
)

const accountNameStyle = computed(() =>
  formatFontStyle(data.components?.accountName.style.fontProperties)
)
const commentStyle = computed(() => ({
  ...formatCommentsComponentStyle(data.components?.comment.style),
  colors: data.components?.comment.style.colors,
}))

const inputTextStyle = computed(() => {
  const fontStyle = formatFontStyle(
    data.components?.comment.style.fontProperties
  )
  return {
    ...fontStyle,
  }
})
const primaryButtonStyle = computed(() =>
  formatCommentsComponentStyle(data.components?.primaryButton.style)
)
const secondaryButtonStyle = computed(() =>
  formatCommentsComponentStyle(data.components?.secondaryButton.style)
)
const engagementsStyle = computed(() =>
  formatCommentsComponentStyle(data.components?.engagements.style)
)

const likeIcon = computed(() =>
  engagements.value.props.like.icon
    ? formatIconStyle(engagements.value.props.like.icon)
    : undefined
)
const dislikeIcon = computed(() =>
  engagements.value.props.dislike.icon
    ? formatIconStyle(engagements.value.props.dislike.icon)
    : undefined
)
const replyIcon = computed(() =>
  engagements.value.props.reply.icon
    ? formatIconStyle(engagements.value.props.reply.icon)
    : undefined
)

const orientation = () => {
  return window.matchMedia("(orientation: portrait)").matches
    ? "portrait"
    : "landscape"
}

const isPortrait = ref("landscape")

const handleResize = () => {
  isPortrait.value = orientation()
}

onMounted(() => {
  isPortrait.value = orientation()
  window.addEventListener("resize", handleResize)

  const vpHeader = document.querySelector(".vp-header")

  if (vpHeader) headerHeight.value = vpHeader.clientHeight
})

// await useAsyncData

if (!isCLH.value) {
  useLazyAsyncData(
    async () => {
      const params = {
        take: take.value,
        page: page.value,
        order: 2,
      }

      // let response = null;
      try {
        const {
          comments: response,
          mentions: mtns,
          canComment: isBlocked,
        } = await pageBuilderStore.actionGetMediaComments(params)
        commentsData.value.numberOfComments = response?.totalCount
        canComment.value = isBlocked
        commentsData.value.data = response.items.map((rawComment) => {
          const comment = new Comments()
          comment.fromData(rawComment)
          return comment
        })

        mentions.value = mtns
        take.value = response.pageSize
        page.value = response.currentPage
        count.value = response.totalCount
        pages.value = response.totalPages
      } catch (error) {
        console.error(error)
      }
    },
    {
      server: false,
    }
  )
}

const loadMoreComments = async () => {
  if (page.value + 1 >= pages.value) return
  page.value++
  const params = {
    take: take.value,
    page: page.value,
    order: 2,
  }
  const { comments: response, mentions: mtns } =
    await pageBuilderStore.actionGetMediaComments(params)
  if (!response || response.length <= 0) return

  if (commentsData?.value?.data)
    commentsData?.value?.data.push(
      ...response.items.map((rawComment) => {
        const comment = new Comments()
        comment.fromData(rawComment)
        return comment
      })
    )

  // mentions.value.concat(mtns)
  const s = new Set(mentions.value.concat(mtns).map((obj) => obj.userId))
  const newMentions = Array.from(s).map((userId) => {
    return mentions.value.concat(mtns).find((obj) => obj.userId === userId)
  })
  mentions.value = newMentions
  take.value = response.pageSize
  page.value = response.currentPage
  count.value = response.totalCount
  pages.value = response.totalPages
}

const loadReplies = async (commentId) => {
  const indexFound = commentsData.value.data.findIndex(
    (c) => c.id === commentId
  )
  const comment = commentsData.value.data[indexFound]
  if (comment.replies.length) return
  const repliesArray = await pageBuilderStore.actionGetCommentReplies(commentId)
  repliesArray.map((rawReply) => {
    const reply = new Comments()
    reply.fromData(rawReply)
    return reply
  })

  if (repliesArray.length) {
    if (indexFound !== -1) {
      commentsData.value.data[indexFound].replies = repliesArray
    }
  }
}

const postCommentCallback = async (data, parentId) => {
  const requiresLogin = await authBuilderStore.actionRequireLogin()

  if (requiresLogin) return

  if (!getCurrentUserAsAuthor) {
    throw "No current user"
  }

  try {
    const response = await pageBuilderStore.actionPostComment(data, parentId)

    if (!response) {
      throw "comment not set"
    }

    response.canDelete = true
    if (!parentId) {
      response.author = getCurrentUserAsAuthor.value

      const comment = new Comments()
      comment.fromData(response)

      commentsData.value.data.unshift(comment)
      commentsData.value.numberOfComments++
    } else {
      //handle reply
      response.author = getCurrentUserAsAuthor.value

      const reply = new Reply()
      reply.fromData(response)

      const indexFound = commentsData.value.data.findIndex(
        (c) => c.id === reply.parentId
      )

      if (indexFound !== -1) {
        commentsData.value.data[indexFound].repliesCount++
        commentsData.value.data[indexFound].replies.push(reply)
      }
    }

    if (window.vpanalytics) {
      vpanalytics.notifyHandlers("onVideoComment")
    }
  } catch (error) {
    console.error(error)
  }
}

const deleteCommentCallback = async (commentId, parentId) => {
  if (!getCurrentUserAsAuthor) {
    throw "No current user"
  }

  try {
    pageBuilderStore
      .actionDeleteComment(commentId)
      .then(() => {
        // Check if there is a parentId provided
        if (parentId) {
          // Find the index of the parent comment
          const parentIndex = commentsData.value.data.findIndex(
            (c) => c.id === parentId
          )
          if (parentIndex === -1) return // Return early if the parent comment is not found

          // Find the index of the child comment within the parent comment
          const childIndex = commentsData.value.data[
            parentIndex
          ].replies.findIndex((c) => c.id === commentId)
          if (childIndex === -1) return // Return early if the child comment is not found

          // Remove the child comment from the replies array of the parent comment
          commentsData.value.data[parentIndex].replies.splice(childIndex, 1)
          commentsData.value.data[parentIndex].repliesCount--
        } else {
          // Find the index of the comment at the top level
          const topLevelIndex = commentsData.value.data.findIndex(
            (c) => c.id === commentId
          )
          if (topLevelIndex === -1) return // Return early if the top-level comment is not found

          // Remove the comment from the top-level array
          commentsData.value.data.splice(topLevelIndex, 1)
          commentsData.value.numberOfComments--
        }
      })
      .catch((err) => {
        throw err
      })
  } catch (error) {
    console.error(error)
  }
}

const isProcessing = ref(false)

const onCommentEngage = async (data, id) => {
  // If an action is currently processing, exit the function early
  if (isProcessing.value) return

  // Set the processing flag to true to indicate the action is ongoing
  isProcessing.value = true

  try {
    // Wrap in a try-catch to handle any errors and ensure isProcessing.value is reset
    const requiresLogin = await authBuilderStore.actionRequireLogin()
    if (requiresLogin) {
      isProcessing.value = false
      return
    }

    let isComment = true
    let indexFound = commentsData.value.data.findIndex((c) => c.id === id)

    const repliesArray = []
    if (indexFound === -1) {
      isComment = false
      commentsData.value.data.map((c) => repliesArray.push(...c.replies))
      indexFound = repliesArray.findIndex((r) => r.id === id)
      if (indexFound === -1) {
        isProcessing.value = false
        return
      }
    }

    const target = isComment
      ? commentsData.value.data[indexFound]
      : repliesArray[indexFound]

    if (data === 1) {
      // Like
      if (target.isLiked) {
        await pageBuilderStore.actionDeleteUserEngagement(data, 3, id)
        target.isLiked = false
        target.likeCount -= 1
      } else {
        await pageBuilderStore.actionPostUserEngagement(data, 3, id)
        if (target.isDisliked) {
          target.isDisliked = false
        }
        target.isLiked = true
        target.likeCount += 1
      }
    } else if (data === 2) {
      // Dislike
      if (target.isDisliked) {
        await pageBuilderStore.actionDeleteUserEngagement(data, 3, id)
        target.isDisliked = false
      } else {
        await pageBuilderStore.actionPostUserEngagement(data, 3, id)
        if (target.isLiked) {
          target.likeCount -= 1
          target.isLiked = false
        }
        target.isDisliked = true
      }
    }
  } catch (error) {
    // console.error(error);
  } finally {
    // No matter the outcome (success or error), reset the processing flag to false
    isProcessing.value = false
  }
}
</script>

<template>
  <div>
    <JComments
      v-if="getProjectId !== 'lGqEarlNem9' && !isCLH"
      :data="commentsData"
      :loadMoreCommentsCallback="loadMoreComments"
      :postCommentCallback="postCommentCallback"
      :deleteCommentCallback="deleteCommentCallback"
      :onCommentEngagementCallback="onCommentEngage"
      :user="getCurrentUserAsAuthor"
      :breakpoint="deviceType"
      :mentionUsers="mentions"
      :mentionEnabled="true"
      :screenOrientation="isPortrait"
      :commentsText="$$t('component_comments_comments')"
      :cancelText="$$t('component_comments_cancel')"
      :confirmText="$$t('component_comments_confirm')"
      :replyText="$$t('component_comments_reply')"
      :repliesText="$$t('component_comments_replies')"
      :style="style"
      :title-style="titleStyle"
      :title-wrapper-style="titleWrapperStyle"
      :account-name-style="accountNameStyle"
      :comment-style="commentStyle"
      :primary-button-style="primaryButtonStyle"
      :secondary-button-style="secondaryButtonStyle"
      :engagements-style="engagementsStyle"
      :show-like="engagements.props.like.type !== 3"
      :show-dislike="engagements.props.dislike.type !== 3"
      :show-reply="engagements.props.reply.type !== 3"
      :show-like-icon="engagements.props.like.buttonType !== 'text'"
      :show-like-text="engagements.props.like.buttonType !== 'icon'"
      :show-like-count="engagements.props.like.type === 1"
      :show-dislike-icon="engagements.props.dislike.buttonType !== 'text'"
      :show-dislike-text="engagements.props.dislike.buttonType !== 'icon'"
      :show-dislike-count="engagements.props.dislike.type === 1"
      :show-reply-icon="engagements.props.reply.buttonType !== 'text'"
      :show-reply-text="engagements.props.reply.buttonType !== 'icon'"
      :show-reply-count="engagements.props.reply.type === 1"
      :like-icon="likeIcon"
      :dislike-icon="dislikeIcon"
      :reply-icon="replyIcon"
      :escapeHeight="headerHeight"
      :loadRepliesCallback="loadReplies"
      :dateFormat="global.language.locale"
      :deleteText="$$t('component_comments_delete')"
    >
      <template #commentInput>
        <JCommentInput
          :callback="postCommentCallback"
          :user="getCurrentUserAsAuthor"
          :mentionUsers="mentions"
          :mentionEnabled="true"
          :cancelText="$$t('component_comments_cancel')"
          :confirmText="$$t('component_comments_confirm')"
          :primary-button-style="primaryButtonStyle"
          :secondary-button-style="secondaryButtonStyle"
          :canComment="canComment"
          :input-text-style="inputTextStyle"
        />
      </template>
    </JComments>
  </div>
</template>

<style></style>
